/**
 * The query string each list route was last showing, so a sidebar click can put
 * you back in the view you were working in rather than on a reset page 1.
 *
 * sessionStorage, deliberately: remembering a filtered view for the rest of the
 * tab session is helpful, while remembering it until next week is the "why is my
 * data missing?" trap — a list silently filtered by something you set days ago
 * reads as broken. A tab close is a clean slate.
 *
 * It is never invisible either way: the restored query is in the URL and the
 * page's own chip row names every active filter.
 */
const PREFIX = 'lastListQuery:';

/** sessionStorage throws in private windows and with site data blocked, and a
 *  forgotten filter is never worth breaking navigation over. */
export function rememberListQuery(pathname: string, search: string): void {
  try {
    if (search) sessionStorage.setItem(PREFIX + pathname, search);
    else sessionStorage.removeItem(PREFIX + pathname);
  } catch {
    /* no-op */
  }
}

/** The remembered query for a route, or '' when there is none. */
export function recallListQuery(pathname: string): string {
  try {
    return sessionStorage.getItem(PREFIX + pathname) ?? '';
  } catch {
    return '';
  }
}

export function forgetListQuery(pathname: string): void {
  try {
    sessionStorage.removeItem(PREFIX + pathname);
  } catch {
    /* no-op */
  }
}

/**
 * Where a sidebar click should land: the remembered view normally, a clean slate
 * when you are already on that route. Clicking the section you are in is the
 * natural "start over" gesture, and having one makes remembering safe rather
 * than something you have to fight.
 */
export function resolveNavTarget(targetPath: string, currentPath: string): string {
  if (targetPath === currentPath) {
    forgetListQuery(targetPath);
    return targetPath;
  }
  const remembered = recallListQuery(targetPath);
  return remembered ? `${targetPath}?${remembered}` : targetPath;
}
